import { EMAIL_CAMPAIGN_CONST } from "./emailCampaignConstants";

type EmailCampaignRecord = Record<string, any>;

function isLocationHasValidSelection(loc?: EmailCampaignRecord) {
  if (loc?.template && loc?.schedule_type) {
    if (
      loc?.schedule_type ===
        EMAIL_CAMPAIGN_CONST.SCHEDULED_TYPE.SCHEDULED.value &&
      (!loc?.schedule_date || !loc?.schedule_time)
    ) {
      return false;
    }
    return true;
  }
  return false;
}

function getFilledLocations(values: Record<string, EmailCampaignRecord> = {}) {
  return Object.keys(values).reduce((t, k) => {
    const loc = values[k];
    if (isLocationHasValidSelection(loc)) {
      return { ...t, [k]: values[k] };
    } else {
      return t;
    }
  }, {});
}

function hasInvalidateSelection(
  values: Record<string, EmailCampaignRecord> = {}
) {
  const filledLocs = getFilledLocations(values);
  return Object.keys(filledLocs).some((key) => {
    const loc = filledLocs[key];
    return !isLocationHasValidSelection(loc);
  });
}

/**
 * Converts between numeric and percentage values
 * @param {number|string} value - The value to convert
 * @param {string} targetType - 'number' or 'percentage'
 * @param {number} total - The total value for percentage calculation (optional)
 * @returns {string} - Formatted value
 */
export const convertValue = (
  value: number | string | null | undefined,
  targetType: string,
  total: number | null = null
) => {
  // Handle null/undefined values
  if (value === null || value === undefined) return "0";

  // Convert to number if it's a string
  let numericValue =
    typeof value === "string" ? parseFloat(value.replace(/[%,]/g, "")) : value;

  // Handle NaN
  if (Number.isNaN(numericValue)) return "0";

  if (targetType === "percentage") {
    // Convert number to percentage
    if (total && total > 0) {
      const percentage = (numericValue / total) * 100;
      return `${percentage.toFixed(2)}%`;
    } else {
      // If no total provided, assume it's already a percentage
      return `${numericValue.toFixed(2)}%`;
    }
  } else if (targetType === "number") {
    // Convert percentage to number
    if (typeof value === "string" && value.includes("%")) {
      // Remove % and convert to number
      return numericValue.toLocaleString();
    } else {
      // Already a number, just format it
      return numericValue.toLocaleString();
    }
  }

  return "0";
};

/**
 * Calculates percentage from a value and total
 * @param {number} value - The value
 * @param {number} total - The total value
 * @returns {string} - Formatted percentage
 */
export const calculatePercentage = (value: number, total: number) => {
  if (!total || total === 0) return "0%";
  const percentage = (value / total) * 100;
  return `${percentage.toFixed(2)}%`;
};

/**
 * Formats number with commas
 * @param {number} value - The number to format
 * @returns {string} - Formatted number
 */
export const formatNumber = (value: number) => {
  if (!value) return "0";
  return value.toLocaleString();
};

export const emailCampaignHelper = {
  getFilledLocations,
  hasInvalidateSelection,
  isLocationHasValidSelection,
};
